import { recoverOutbox } from '@/lib/outboxRecovery';
import { supabase } from "@/integrations/supabase/client";

export type TaskType =
  | "GENERATE_APPRAISAL_PDF"
  | "SEND_APPRAISAL_EMAIL"
  | "GENERATE_INVOICE_PDF"
  | "SEND_INVOICE_EMAIL"
  | "GENERATE_CONSOLIDATED_INVOICE_PDF"
  | "SEND_CONSOLIDATED_INVOICE_EMAIL"
  | "GENERATE_DAILY_CHECK_PDF"
  | "SEND_SMS"
  | "SYNC_QUICKBOOKS_INVOICE"
  | "SYNC_XERO_INVOICE"
  | "SYNC_FREEAGENT_INVOICE"
  | "CALCULATE_ROUTE";

export type TaskStatus = "pending" | "processing" | "succeeded" | "failed" | "cancelled";

export interface OutboxTask {
  id: string;
  task_type: TaskType;
  job_id: string | null;
  invoice_id: string | null;
  appraisal_id: string | null;
  appraisal_type: string | null;
  payload: Record<string, unknown>;
  status: TaskStatus;
  attempt_count: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  result: Record<string, unknown> | null;
  company_id: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface EnqueueOptions {
  taskType: TaskType;
  jobId?: string;
  invoiceId?: string;
  appraisalId?: string;
  appraisalType?: "collection" | "delivery";
  payload?: Record<string, unknown>;
  companyId?: string;
  userId?: string;
}

/**
 * Enqueue a task for background processing with automatic retry.
 * Returns the task ID if enqueued, or null if duplicate prevented.
 */
export async function enqueueTask(options: EnqueueOptions): Promise<string | null> {
  const {
    taskType,
    jobId,
    invoiceId,
    appraisalId,
    appraisalType,
    payload = {},
    companyId,
    userId,
  } = options;

  try {
    // Use the database function for atomic insertion with duplicate prevention
    const { data, error } = await supabase.rpc("enqueue_task", {
      p_task_type: taskType,
      p_job_id: jobId || null,
      p_invoice_id: invoiceId || null,
      p_appraisal_id: appraisalId || null,
      p_appraisal_type: appraisalType || null,
      p_payload: payload as unknown as Record<string, never>,
      p_company_id: companyId || null,
      p_user_id: userId || null,
    });

    if (error) {
      console.error("[OutboxQueue] Failed to enqueue task:", error);
      // Don't throw - return null to indicate it wasn't enqueued
      return null;
    }

    console.log(`[OutboxQueue] Enqueued task ${taskType}:`, data);
    return data;
  } catch (err) {
    console.error("[OutboxQueue] Error enqueuing task:", err);
    return null;
  }
}

/**
 * Attempt immediate processing of pending tasks.
 * Used when backend recovers from an outage.
 * 
 * Note: In Phase 1, this will fail if edge functions aren't deployed.
 * The cron job or manual Phase 2 deployment will process tasks later.
 */
export async function triggerOutboxProcessing(batchSize = 10): Promise<{
  success: boolean;
  processed?: number;
  succeeded?: number;
  failed?: number;
  results?: Array<{ taskId: string; success: boolean; error?: string }>;
}> {
  try {
    const data = await recoverOutbox('drain', batchSize);

    console.log("[OutboxQueue] Outbox processing result:", data);
    return {
      success: data?.ok || false,
      processed: data?.processed,
      succeeded: data?.succeeded,
      failed: data?.failed,
      results: data?.results,
    };
  } catch (err: any) {
    // Expected in Phase 1
    console.log("[OutboxQueue] Outbox processing not available:", err?.message);
    return { success: false };
  }
}

/**
 * Get pending tasks for a specific job (for display in UI)
 */
export async function getJobTasks(jobId: string): Promise<OutboxTask[]> {
  const { data, error } = await supabase
    .from("outbox_tasks")
    .select("*")
    .eq("job_id", jobId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[OutboxQueue] Failed to get job tasks:", error);
    return [];
  }

  return (data || []) as OutboxTask[];
}

/**
 * Get pending tasks for a specific appraisal (for display in UI)
 */
export async function getAppraisalTasks(appraisalId: string): Promise<OutboxTask[]> {
  const { data, error } = await supabase
    .from("outbox_tasks")
    .select("*")
    .eq("appraisal_id", appraisalId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[OutboxQueue] Failed to get appraisal tasks:", error);
    return [];
  }

  return (data || []) as OutboxTask[];
}

/**
 * Retry a failed task by resetting its status
 */
export async function retryTask(taskId: string): Promise<boolean> {
  const { error } = await supabase
    .from("outbox_tasks")
    .update({
      status: "pending",
      next_attempt_at: new Date().toISOString(),
      last_error: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", taskId);

  if (error) {
    console.error("[OutboxQueue] Failed to retry task:", error);
    return false;
  }

  return true;
}

/**
 * Cancel a pending task
 */
export async function cancelTask(taskId: string): Promise<boolean> {
  const { error } = await supabase
    .from("outbox_tasks")
    .update({
      status: "cancelled",
      updated_at: new Date().toISOString(),
    })
    .eq("id", taskId)
    .in("status", ["pending", "processing"]);

  if (error) {
    console.error("[OutboxQueue] Failed to cancel task:", error);
    return false;
  }

  return true;
}

/**
 * Get all pending/failed tasks for admin view
 */
export async function getOutboxTasks(options?: {
  status?: TaskStatus[];
  limit?: number;
  companyId?: string;
}): Promise<OutboxTask[]> {
  let query = supabase
    .from("outbox_tasks")
    .select("*")
    .order("created_at", { ascending: false });

  if (options?.status && options.status.length > 0) {
    query = query.in("status", options.status);
  }

  if (options?.companyId) {
    query = query.eq("company_id", options.companyId);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[OutboxQueue] Failed to get outbox tasks:", error);
    return [];
  }

  return (data || []) as OutboxTask[];
}

/**
 * Get backlog statistics for admin view
 */
export async function getOutboxStats(): Promise<{
  pendingCount: number;
  processingCount: number;
  failedCount: number;
  oldestPendingAt: string | null;
}> {
  try {
    // Get counts by status
    const { data: pending } = await supabase
      .from("outbox_tasks")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending");

    const { data: processing } = await supabase
      .from("outbox_tasks")
      .select("id", { count: "exact", head: true })
      .eq("status", "processing");

    const { data: failed } = await supabase
      .from("outbox_tasks")
      .select("id", { count: "exact", head: true })
      .eq("status", "failed");

    // Get oldest pending task
    const { data: oldest } = await supabase
      .from("outbox_tasks")
      .select("created_at")
      .eq("status", "pending")
      .order("created_at", { ascending: true })
      .limit(1)
      .single();

    return {
      pendingCount: (pending as any)?.length ?? 0,
      processingCount: (processing as any)?.length ?? 0,
      failedCount: (failed as any)?.length ?? 0,
      oldestPendingAt: oldest?.created_at || null,
    };
  } catch (err) {
    console.error("[OutboxQueue] Failed to get stats:", err);
    return {
      pendingCount: 0,
      processingCount: 0,
      failedCount: 0,
      oldestPendingAt: null,
    };
  }
}

/**
 * Retry all failed tasks by resetting their status
 */
export async function retryAllFailedTasks(): Promise<number> {
  const { data, error } = await supabase
    .from("outbox_tasks")
    .update({
      status: "pending",
      next_attempt_at: new Date().toISOString(),
      last_error: null,
      updated_at: new Date().toISOString(),
    })
    .eq("status", "failed")
    .select("id");

  if (error) {
    console.error("[OutboxQueue] Failed to retry all tasks:", error);
    return 0;
  }

  return data?.length || 0;
}
